import React, { useEffect, useState } from 'react';
import { Zap, Users, Gift, TrendingUp, CheckCircle, Sparkles, ArrowRight, AlertCircle, DollarSign, ShieldCheck, Lock, Award, LogOut } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';

interface PaywallStats {
  totalWheels: number;
  totalLeads: number;
  totalSpins: number;
}

const FreePlanPaywall: React.FC = () => {
  const { logout } = useAuth();
  const [stats, setStats] = useState<PaywallStats>({
    totalWheels: 0,
    totalLeads: 0,
    totalSpins: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const response = await api.get('/wheels/stats');

      setStats({
        totalWheels: response.data.totalWheels || 0,
        totalLeads: response.data.totalLeads || 0,
        totalSpins: response.data.totalSpins || 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <LoadingSpinner />
      </div>
    );
  }

  const potentialRevenue = stats.totalLeads * 50; // Estimativa conservadora de R$ 50 por lead
  const conversionRate = stats.totalSpins > 0 ? ((stats.totalLeads / stats.totalSpins) * 100).toFixed(1) : '0';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Botão Sair */}
        <div className="flex justify-end mb-4">
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
        {/* Header com Crown Icon */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center w-28 h-28 sm:w-40 sm:h-40 mb-4 sm:mb-6">
            <Logo className="h-14 sm:h-20" />
          </div>

          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4">
            Desbloqueie Todo o Potencial do
            <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Roda de Prêmios
            </span>
          </h1>
        </div>

        {/* Aviso de que o plano gratuito não existe mais */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-5 sm:p-8 mb-8 sm:mb-12 text-white shadow-xl">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 bg-white/20 rounded-lg backdrop-blur-sm flex-shrink-0">
              <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <div>
              <h3 className="text-lg sm:text-2xl font-bold mb-2">O plano gratuito não está mais disponível</h3>
              <p className="text-red-50 text-sm sm:text-lg mb-2">
                Para continuar utilizando o Roda de Prêmios e ter acesso a todas as funcionalidades,
                é necessário assinar o <strong>Plano Pro</strong>.
              </p>
              <p className="text-red-100 text-xs sm:text-base">
                Faça o upgrade agora e não perca seus dados e clientes capturados!
              </p>
            </div>
          </div>
        </div>

        {/* Estatísticas do Usuário */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-12">
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 border-purple-100">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-2 sm:mb-4 gap-2">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
                <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="text-center sm:text-right">
                <p className="text-xl sm:text-3xl font-black text-gray-900">{stats.totalWheels}</p>
                <p className="text-xs sm:text-sm text-gray-500">Sorteios</p>
              </div>
            </div>
            <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-purple-600 w-full"></div>
            </div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 border-pink-100">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-2 sm:mb-4 gap-2">
              <div className="p-2 sm:p-3 bg-pink-100 rounded-lg">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" />
              </div>
              <div className="text-center sm:text-right">
                <p className="text-xl sm:text-3xl font-black text-gray-900">{stats.totalLeads}</p>
                <p className="text-xs sm:text-sm text-gray-500">Clientes</p>
              </div>
            </div>
            <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink-500 to-pink-600 w-full"></div>
            </div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg border-2 border-indigo-100">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-2 sm:mb-4 gap-2">
              <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div className="text-center sm:text-right">
                <p className="text-xl sm:text-3xl font-black text-gray-900">{stats.totalSpins}</p>
                <p className="text-xs sm:text-sm text-gray-500">Participações</p>
              </div>
            </div>
            <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 w-full"></div>
            </div>
          </div>
        </div>

        {/* Insight Box */}
        {stats.totalLeads > 0 && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-5 sm:p-8 mb-8 sm:mb-12 text-white shadow-xl">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-white/20 rounded-lg backdrop-blur-sm flex-shrink-0">
                <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-bold mb-2 flex items-center gap-2">Você já capturou {stats.totalLeads} clientes! <Award className="w-5 h-5 sm:w-7 sm:h-7" /></h3>
                <p className="text-green-50 text-sm sm:text-lg mb-3 sm:mb-4">
                  Com uma taxa de conversão de {conversionRate}%, você está construindo uma base valiosa de clientes.
                </p>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <p className="text-sm font-semibold mb-1 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Potencial estimado da sua lista:</p>
                  <p className="text-2xl sm:text-3xl font-black">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(potentialRevenue)}
                  </p>
                  <p className="text-xs text-green-100 mt-1">
                    *Baseado em R$ 50 de ticket médio por cliente
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recursos do Plano Pro */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-8 sm:mb-12 border-2 border-purple-200">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-5 sm:px-8 py-4 sm:py-6">
            <h2 className="text-xl sm:text-3xl font-black text-white text-center">
              Tudo Que Você Terá com o Plano Pro
            </h2>
          </div>

          <div className="p-5 sm:p-8 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-purple-900">Plano Pro</h3>
              <span className="ml-auto bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                RECOMENDADO
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Até 5 sorteios ativos</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Até 200 produtos/prêmios</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">3000 giros por mês</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Integração com WhatsApp</span>
                </li>
              </ul>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Temas personalizados</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Exportação de relatórios</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Suporte prioritário</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Sem marca d'água</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pricing e CTA */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-purple-600">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-5 sm:px-8 py-4 sm:py-6 text-center">
            <p className="text-purple-100 text-xs sm:text-sm font-semibold uppercase tracking-wide mb-2">
              Oferta Especial
            </p>
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <div className="text-left">
                <p className="text-white/80 line-through text-lg sm:text-2xl">R$ 79,90</p>
              </div>
              <div className="text-center">
                <p className="text-white text-4xl sm:text-6xl font-black">R$ 9,90</p>
                <p className="text-purple-100 text-base sm:text-lg">/mês</p>
              </div>
            </div>
            <p className="text-purple-100 mt-2 text-sm sm:text-base">
              ou <strong className="text-white">R$ 99,00/ano</strong> - economize 2 meses!
            </p>
          </div>

          <div className="p-5 sm:p-8">
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Por que fazer upgrade agora?
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                  <Zap className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Resultados Imediatos</p>
                    <p className="text-sm text-gray-600">Ative recursos poderosos instantaneamente</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-pink-50 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-pink-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Aumente suas Vendas</p>
                    <p className="text-sm text-gray-600">Empresas Pro convertem 3x mais</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Fidelize Clientes</p>
                    <p className="text-sm text-gray-600">Mantenha contato via WhatsApp</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Garantia Total</p>
                    <p className="text-sm text-gray-600">7 dias para testar sem riscos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Principal */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <a
                href="https://pay.kiwify.com.br/fuvPJn1"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-center px-4 sm:px-6 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg shadow-xl transform hover:scale-105 transition-all duration-200 group"
              >
                <span className="flex flex-col items-center gap-1">
                  <span className="flex items-center gap-2">
                    Plano Mensal
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="text-sm font-medium text-purple-100">R$ 9,90/mês</span>
                </span>
              </a>
              <a
                href="https://pay.kiwify.com.br/Zyfotu1"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-center px-4 sm:px-6 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg shadow-xl transform hover:scale-105 transition-all duration-200 group relative overflow-hidden"
              >
                <span className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-bl-lg">MELHOR OFERTA</span>
                <span className="flex flex-col items-center gap-1">
                  <span className="flex items-center gap-2">
                    Plano Anual
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="text-sm font-medium text-green-100">R$ 99,00/ano (economize 2 meses!)</span>
                </span>
              </a>
            </div>

            <div className="mt-6 text-center space-y-2">
              <p className="text-xs sm:text-sm text-gray-600 flex flex-wrap items-center justify-center gap-2 sm:gap-4">
                <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" /> Ativação imediata</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" /> Cancele quando quiser</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" /> Garantia de 7 dias</span>
              </p>
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Pagamento seguro via Kiwify • Milhares de empresas confiam
              </p>
            </div>
          </div>
        </div>

        {/* Footer de Segurança */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-gray-500 text-sm flex items-center justify-center gap-1.5">
            <Lock className="w-4 h-4" /> Pagamento 100% seguro e criptografado
          </p>
        </div>
      </div>
    </div>
  );
};

export default FreePlanPaywall;
